/* eslint-disable */
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { bindActionCreators } from "redux";
import { ArrowLeft, ExternalLink, Download, FileText, AlertTriangle } from "lucide-react";
import FileSaver from "file-saver";
import RenderSupportPage from "../RenderSupportPage";
import PageSkeleton from "../../Components/PageSkeleton";
import LoanActivityTrail from "../components/LoanActivityTrail";
import { dataActionCreators } from "../../services/Actions";
import { GhanaMoneyDisplay } from "../../helpers/helper";

// Read-only view of a member's loan application form for the support desk —
// exactly what the member filled in, plus the activity trail, so an agent can
// answer "what did I put down as my guarantor / why was it rejected?" on a call
// without holding the loans role. Deliberately no Book/Authorize/Reject/
// Refinance/Message actions: this desk answers questions, it does not action
// loans (same rule as /support/loan-status).
//
// The loan id travels in the query string rather than router state, because an
// agent on a call refreshes the page and `location.state` does not survive that.

const STATUS_STYLES = {
  submitted: "bg-blue-50 dark:bg-blue-900/25 text-blue-700 dark:text-blue-300 border-blue-100 dark:border-blue-800",
  booked: "bg-green-50 dark:bg-green-900/25 text-green-700 dark:text-green-300 border-green-100 dark:border-green-800",
  authorized: "bg-teal-50 dark:bg-teal-900/25 text-teal-700 dark:text-teal-300 border-teal-100 dark:border-teal-800",
  initiated: "bg-amber-50 dark:bg-amber-900/25 text-amber-700 dark:text-amber-300 border-amber-100 dark:border-amber-800",
  paid: "bg-emerald-50 dark:bg-emerald-900/25 text-emerald-700 dark:text-emerald-300 border-emerald-100 dark:border-emerald-800",
  rejected: "bg-red-50 dark:bg-red-900/25 text-red-700 dark:text-red-300 border-red-100 dark:border-red-800",
};

// Sections render as label/value rows; a value of null/""/undefined shows a dash
// rather than an empty cell so the agent can tell "not supplied" from "missing".
function Row({ label, value, strong, computed }) {
  const empty = value === null || value === undefined || value === "";
  return (
    <div className="flex justify-between items-start gap-4 py-2 border-b border-slate-50 dark:border-gray-700/60 last:border-b-0">
      <span className="text-sm text-slate-500 dark:text-slate-400 flex-shrink-0">{label}</span>
      {/* An agent reads these figures aloud to a member, so a value the page
          worked out itself must never look identical to one the record holds.
          A leading ≈ (plus the tooltip) marks it without shouting. */}
      <span
        className={`text-right break-words ${strong
          ? "text-base font-bold text-slate-800 dark:text-slate-100"
          : "text-sm font-semibold text-slate-800 dark:text-slate-200"
          } ${empty ? "!font-normal text-slate-300 dark:text-slate-600" : ""}`}
        title={
          computed && !empty
            ? "Not stored on this application — worked out from the monthly deduction × loan term"
            : undefined
        }
      >
        {computed && !empty && (
          <span className="mr-0.5 font-normal text-slate-400 dark:text-slate-500">≈</span>
        )}
        {empty ? "—" : value}
      </span>
    </div>
  );
}

function Card({ title, children, className = "" }) {
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 overflow-hidden ${className}`}>
      <div className="px-5 py-3 bg-slate-50 dark:bg-gray-900/50 border-b border-slate-200 dark:border-gray-700">
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          {title}
        </h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

// Joins the two address lines the form collects into one displayable line.
const joinAddress = (line1, line2) =>
  [line1, line2].filter((p) => p !== null && p !== undefined && p !== "").join(", ");

const toAmount = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Applications written before `initialAmount` / `loantotalCollectable` were
 * added to the schema carry neither, so those rows come back blank on older
 * loans. Both are recoverable from what those records do hold: the backend
 * defines the total collectable as monthlyDeduction x loanTerm, and the
 * monthly deduction as initialAmount / loanTerm — so the same product
 * reproduces each (the initial amount up to the 2dp rounding on the deduction).
 *
 * Returns `{ value, computed }` so the caller can mark a derived figure.
 */
const storedOrDerived = (stored, monthlyDeduction, loanTerm) => {
  const direct = toAmount(stored);
  if (direct !== null) return { value: direct.toFixed(2), computed: false };

  const md = toAmount(monthlyDeduction);
  const term = toAmount(loanTerm);
  if (md === null || term === null || term <= 0) return { value: null, computed: false };
  return { value: (md * term).toFixed(2), computed: true };
};

function LoanApplicationDetailed() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { GetLoanDetailed, ResetDataResponse } = bindActionCreators(
    dataActionCreators,
    dispatch
  );

  const loan_id = new URLSearchParams(location.search).get("loan_id");
  const data = useSelector((state) => state?.data);
  const { loan_detailed, isLoading, response } = data;

  useEffect(() => {
    if (loan_id) {
      // `response` is shared across the whole data slice — an error raised by
      // the page we came from would otherwise show here as this loan's failure.
      ResetDataResponse();
      GetLoanDetailed(loan_id);
    }
  }, [loan_id]);

  // `loan_detailed` lives in the shared data slice and survives navigation, so
  // the previously opened loan would flash before this one arrives. Only treat
  // it as ours once the id matches.
  const loan = loan_detailed?._id === loan_id ? loan_detailed : null;
  const failed = !loan && !isLoading && response?.state === "ERROR";

  // The fetch failure leaves the stale loan in the store; clear the flag on the
  // way out so the next page doesn't inherit this error toast state.
  useEffect(() => () => ResetDataResponse(), []);

  // Initial Amount is the same quantity as the total collectable (principal +
  // interest + fee), differing only by the rounding applied to the monthly
  // deduction — so both fall back to the same product when the record has no
  // stored value.
  const initialAmount = storedOrDerived(
    loan?.initialAmount,
    loan?.monthlyDeduction,
    loan?.loanTerm
  );
  const totalCollectable = storedOrDerived(
    loan?.loantotalCollectable,
    loan?.monthlyDeduction,
    loan?.loanTerm
  );

  const guarantor2Filled =
    loan?.guarantor_name2 ||
    loan?.guarantor_phone2 ||
    loan?.guarantor_staff_id2 ||
    loan?.guarantor_ghana_card2;

  return (
    <RenderSupportPage title="Loan Application">
      <button
        onClick={() => history.goBack()}
        className="inline-flex items-center gap-2 -mt-4 mb-5 px-3 py-2 rounded-lg text-sm font-semibold text-slate-600 dark:text-slate-300 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-600 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
      >
        <ArrowLeft size={15} /> Back
      </button>

      {!loan_id ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 px-5 py-16 text-center">
          <FileText size={30} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
            No loan selected. Open one from Loan Status.
          </p>
        </div>
      ) : failed ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-red-200 dark:border-red-800 px-5 py-16 text-center">
          <AlertTriangle size={30} className="mx-auto text-red-400 mb-3" />
          <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">
            {response?.message || "Could not load this loan application."}
          </p>
          <button
            onClick={() => GetLoanDetailed(loan_id)}
            className="mt-4 px-4 py-2 rounded-lg text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
          >
            Try again
          </button>
        </div>
      ) : !loan ? (
        <PageSkeleton cards={2} rows={6} cols={2} />
      ) : (
        <>
          {/* Member header */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 p-5 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <img
                src={loan?.user?.profile_image || "/images/dev/avarta2.jpeg"}
                alt="Profile"
                className="w-16 h-16 rounded-full object-cover border-2 border-white dark:border-gray-600 shadow-sm flex-shrink-0"
              />
              <div className="min-w-0 flex-1">
                <p className="text-lg font-black text-slate-800 dark:text-slate-100 leading-tight">
                  {loan?.user?.name || loan?.fullname || "—"}
                </p>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-0.5">
                  {loan?.staff_id || loan?.user?.staff_id || "—"}
                  {loan?.user?.facility ? ` · ${loan.user.facility}` : ""}
                </p>
                <span
                  className={`inline-flex items-center mt-2 px-2.5 py-0.5 rounded-full text-xs font-bold border capitalize ${STATUS_STYLES[loan?.status] ||
                    "bg-slate-50 dark:bg-gray-700 text-slate-700 dark:text-slate-300 border-slate-100 dark:border-gray-600"
                    }`}
                >
                  {loan?.status || "—"}
                </span>
              </div>
              {(loan?.staff_id || loan?.user?.staff_id) && (
                <button
                  onClick={() =>
                    history.push(
                      `/support/member-detailed?staff_id=${loan?.staff_id || loan?.user?.staff_id}`
                    )
                  }
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-slate-600 dark:text-slate-300 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-600 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors self-start"
                >
                  <ExternalLink size={15} /> Member profile
                </button>
              )}
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Activity trail spans both columns — it is the first thing an
                agent is asked about, so it leads. */}
            <Card title="Activity Trail" className="md:col-span-2">
              <LoanActivityTrail loan={loan} />
            </Card>

            <Card title="Loan Details">
              <Row label="Loan Amount" strong value={<GhanaMoneyDisplay amount={loan?.loanAmount ?? 0} />} />
              {loan?.rebook && (
                <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-xl p-3 my-2">
                  <div className="flex justify-between items-center gap-3 py-1">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Amount Payable</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-emerald-700 dark:text-emerald-300">
                        {loan?.rebook_amount_payable}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-slate-500">
                        Refinanced
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center gap-3 py-1">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Previous Balance</span>
                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                      {loan?.rebook_previous_amount_balance}
                    </span>
                  </div>
                </div>
              )}
              <Row label="Loan Term" value={loan?.loanTerm ? `${loan.loanTerm} months` : null} />
              <Row label="Monthly Deduction" value={loan?.monthlyDeduction} />
              <Row label="Initial Amount" {...initialAmount} />
              <Row label="Total Collectable" {...totalCollectable} />
              <Row label="Affordability" value={loan?.affordability} />
              <Row label="Mandate Number" value={loan?.mandateNumber} />
              <Row label="OTP Number" value={loan?.otpNumber} />
              <Row label="Loan Purpose" value={loan?.purposeForLoan} />
            </Card>

            <Card title="Personal Information">
              <Row label="Full Name" value={loan?.user?.name || loan?.fullname} />
              <Row label="Staff ID" value={loan?.staff_id || loan?.user?.staff_id} />
              <Row label="Facility" value={loan?.user?.facility} />
              <Row label="Email Address" value={loan?.email} />
              <Row label="Phone Number" value={loan?.personal_phone} />
              <Row label="Date Of Birth" value={loan?.dateOfBirth} />
              <Row label="Age" value={loan?.age} />
              <Row label="Gender" value={loan?.gender} />
            </Card>

            <Card title="Address & Contact">
              <Row
                label="Residential Address"
                value={joinAddress(loan?.residential_address1, loan?.residential_address2)}
              />
              <Row
                label="Work Address"
                value={joinAddress(loan?.work_address1, loan?.work_address2)}
              />
              <Row label="Work Phone" value={loan?.work_phone} />
              <Row label="ID Card Type" value={loan?.IdCardType} />
              <Row label="ID Number" value={loan?.IDCardNumber} />
            </Card>

            <Card title="Bank Information">
              <Row label="SSNIT Number" value={loan?.ssnit} />
              <Row label="Bank Name" value={loan?.bank_name} />
              <Row label="Account Holder" value={loan?.bank_account_holder_name} />
              <Row label="Bank Branch" value={loan?.bank_branch} />
              <Row label="Account Number" value={loan?.bank_account_number} />
            </Card>

            <Card title="Guarantor 1">
              <Row label="Full Name" value={loan?.guarantor_name} />
              <Row label="Phone" value={loan?.guarantor_phone} />
              <Row label="Staff ID" value={loan?.guarantor_staff_id} />
              <Row label="Ghana Card Number" value={loan?.guarantor_ghana_card} />
              <Row label="GPS Address" value={loan?.guarantor_gps} />
              <Row label="Occupation" value={loan?.guarantor_occupation} />
              <Row label="Mandate Number" value={loan?.guarantor_mandate_number} />
              <Row label="OTP Number" value={loan?.guarantor_otp_number} />
              <Row label="Relationship" value={loan?.guarantor_relationship} />
            </Card>

            <Card title="Guarantor 2">
              {guarantor2Filled ? (
                <>
                  <Row label="Full Name" value={loan?.guarantor_name2} />
                  <Row label="Phone" value={loan?.guarantor_phone2} />
                  <Row label="Staff ID" value={loan?.guarantor_staff_id2} />
                  <Row label="Ghana Card Number" value={loan?.guarantor_ghana_card2} />
                  <Row label="GPS Address" value={loan?.guarantor_gps2} />
                  <Row label="Occupation" value={loan?.guarantor_occupation2} />
                  <Row label="Mandate Number" value={loan?.guarantor_mandate_number2} />
                  <Row label="OTP Number" value={loan?.guarantor_otp_number2} />
                  <Row label="Relationship" value={loan?.guarantor_relationship2} />
                </>
              ) : (
                <p className="text-sm text-slate-400 dark:text-slate-500 italic py-2">
                  No second guarantor on this application
                </p>
              )}
            </Card>

            <Card title="Employment">
              <Row label="Employer Name" value={loan?.employer_name} />
              <Row label="Start Date" value={loan?.employment_start_date} />
            </Card>

            <Card title="Ghana Identification Card">
              {loan?.frontIDImage || loan?.passportImage ? (
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { url: loan?.frontIDImage, label: "Front ID", file: "front-id.jpg" },
                    { url: loan?.passportImage, label: "Passport Photo", file: "passport-photo.jpg" },
                  ].map((img) =>
                    img.url ? (
                      <div key={img.label}>
                        <a href={img.url} target="_blank" rel="noreferrer">
                          <img
                            src={img.url}
                            alt={img.label}
                            className="w-full h-auto rounded-xl border border-slate-200 dark:border-gray-600 object-cover"
                          />
                        </a>
                        <button
                          onClick={() => FileSaver.saveAs(img.url, img.file)}
                          className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-gray-700 hover:bg-slate-200 dark:hover:bg-gray-600 transition-colors"
                        >
                          <Download size={13} /> {img.label}
                        </button>
                      </div>
                    ) : null
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-400 dark:text-slate-500 italic py-2">
                  No ID images uploaded
                </p>
              )}
            </Card>
          </div>
        </>
      )}
    </RenderSupportPage>
  );
}

export default LoanApplicationDetailed;
